// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// read.ts — what a good player would work out before touching anything.
//
// THIS MODULE IS THE GAME'S ONE UNFAIR ADVANTAGE OVER A CARDBOARD COPY, and it exists to close a
// trap rather than to be clever. A phone can only show a rival's board in summary, and the bot sees
// all of it — so unless the screen does the reading FOR you, the human ends up playing solitaire
// while the bot denies perfectly. Every number here is derived from public information that a player
// at a table could work out; none of it reveals the bag.

import { COURSES, KINDS, courseCap, spoilPenalty } from './kinds';
import { SILL, SPOIL, Table, canLoad, projectedLayScore, takeCount, type Move } from './rules';

export interface Dest {
  readonly dest: number;
  readonly legal: boolean;
  /** Why not, in the player's own words. */
  readonly reason: string;
  readonly placed: number;
  readonly overflow: number;
  /** What this load will be worth when it lays — 0 if it will not fill this round. */
  readonly lay: number;
  /** Will it fill and lay? */
  readonly fills: boolean;
  /** Extra Spoil cost of the overflow, as a negative number. */
  readonly cost: number;
  /** Freehand: the columns still open to it, and whether it must be nominated now. */
  readonly cols: readonly number[];
  readonly needsCol: boolean;
}

/** Every destination for a take, legal or not — a forbidden row is shown and disabled, never hidden. */
export function destinations(t: Table, seat: number, src: number, kind: number): Dest[] {
  const n = takeCount(t, src, kind);
  const out: Dest[] = [];
  const already = spoilPenalty(t.spoilTiles[seat] + t.trowel[seat]);

  for (let c = 0; c < COURSES; c++) {
    const i = seat * COURSES + c;
    const cap = courseCap(c);
    const room = Math.max(0, cap - t.courseCount[i]);
    const placed = Math.min(n, room);
    const overflow = n - placed;
    const legal = canLoad(t, seat, c, kind);
    const cols = t.mode.printed ? [] : t.legalCols(seat, c, kind);
    const needsCol = !t.mode.printed && t.courseCol[i] === -1;
    let reason = '';
    if (!legal) {
      if ((t.fired[seat] >> c) & 1) reason = 'already laid this round';
      else if (t.courseCount[i] >= cap) reason = 'course is full';
      else if (t.courseKind[i] !== -1 && t.courseKind[i] !== kind) reason = 'holds another kind';
      else if (t.rowHasKind(seat, c, kind)) reason = `already on row ${c + 1}`;
      else reason = 'no free column';
    }
    const willFill = legal && t.courseCount[i] + placed === cap;
    out.push({
      dest: c,
      legal,
      reason,
      placed,
      overflow,
      lay: willFill ? projectedLayScore(t, seat, c, kind) : 0,
      fills: willFill,
      cost: -(spoilPenalty(t.spoilTiles[seat] + t.trowel[seat] + overflow) - already),
      cols,
      needsCol,
    });
  }
  out.push({
    dest: SPOIL,
    legal: true,
    reason: '',
    placed: 0,
    overflow: n,
    lay: 0,
    fills: false,
    cost: -(spoilPenalty(t.spoilTiles[seat] + t.trowel[seat] + n) - already),
    cols: [],
    needsCol: false,
  });
  return out;
}

/** In Freehand, what one column would be worth — and what nominating it forecloses. */
export function columnRead(
  t: Table,
  seat: number,
  course: number,
  kind: number,
  col: number,
): { lay: number; blocks: number[] } {
  const i = seat * COURSES + course;
  const was = t.courseCol[i];
  t.courseCol[i] = col;
  const lay = projectedLayScore(t, seat, course, kind);
  t.courseCol[i] = was;
  // Reserving (course,col) for this kind means this kind can never go in that column again.
  const blocks: number[] = [];
  for (let r = 0; r < COURSES; r++) {
    if (r === course) continue;
    if (t.cell(seat, r, col) === -1 && !t.rowHasKind(seat, r, kind)) blocks.push(r);
  }
  return { lay, blocks };
}

export interface Threat {
  readonly seat: number;
  /** How many more tiles of this kind that seat needs to fill its hungriest course. */
  readonly needs: number;
  /** What it would be worth to them. */
  readonly worth: number;
  readonly course: number;
}

/**
 * Who else is WAITING for this kind, and how badly.
 *
 * "Could legally use it" is not the same thing and is worthless as a signal: at the start of a round
 * every course is empty, so every rival can use every kind and a naive filter names the whole table
 * on every take. What makes a rival a threat is that they have already COMMITTED to that kind — a
 * course part-filled with it — or that it would land somewhere genuinely valuable. Anything else is
 * noise dressed up as information.
 */
export function wanters(t: Table, kind: number, exclude: number): Threat[] {
  const out: Threat[] = [];
  for (let s = 0; s < t.players; s++) {
    if (s === exclude) continue;
    let best: Threat | null = null;
    for (let c = 0; c < COURSES; c++) {
      if (!canLoad(t, s, c, kind)) continue;
      const held = t.courseCount[s * COURSES + c];
      const need = courseCap(c) - held;
      const worth = projectedLayScore(t, s, c, kind);
      const committed = held > 0 && t.courseKind[s * COURSES + c] === kind;
      if (!committed && worth < 3) continue; // they could use it; they are not waiting for it
      if (!best || need < best.needs || (need === best.needs && worth > best.worth)) {
        best = { seat: s, needs: need, worth, course: c };
      }
    }
    if (best) out.push(best);
  }
  return out.sort((a, b) => a.needs - b.needs || b.worth - a.worth);
}

export interface TakeRead {
  readonly count: number;
  /** What tumbles into the Sill if you take this from a rack — kind counts. */
  readonly leaves: readonly { kind: number; count: number }[];
  /** Who is waiting for what you would leave behind. */
  readonly gifts: readonly Threat[];
  /** Who you would be taking it away from. */
  readonly denies: readonly Threat[];
}

/**
 * The whole point of a take, in one object: what you get, what you hand over, and who is waiting for
 * each. "Take the three Woad you need and two Cinnabar tumble into the Sill for the player on your
 * left" is the sentence this game is built on, and this is what puts it on screen.
 */
export function readTake(t: Table, seat: number, src: number, kind: number): TakeRead {
  const count = takeCount(t, src, kind);
  const leaves: { kind: number; count: number }[] = [];
  if (src !== SILL) {
    for (let k = 0; k < KINDS; k++) {
      const n = t.racks[src * KINDS + k];
      if (k !== kind && n > 0) leaves.push({ kind: k, count: n });
    }
  }
  const gifts: Threat[] = [];
  for (const l of leaves) {
    for (const w of wanters(t, l.kind, seat)) {
      if (w.needs <= l.count) gifts.push(w);
    }
  }
  return {
    count,
    leaves,
    gifts: gifts.sort((a, b) => b.worth - a.worth).slice(0, 3),
    denies: wanters(t, kind, seat)
      .filter((w) => w.needs <= count)
      .slice(0, 3),
  };
}

/** The move a destination row commits to. */
export const moveFor = (src: number, kind: number, dest: number, col = -1): Move => ({
  src,
  kind,
  dest,
  col: dest === SPOIL ? -1 : col,
});
